import type { ObjectiveCheckpointAnswer, ObjectiveDateSuggestionView, ObjectiveId, ObjectiveReviewItem } from '@contracts';
import { formatShortDate } from '@ui/shared/format/date';

/**
 * Objectives panel — status check-in (§1.5/§1.5a, WR-12-WR-14). One inner
 * card per active objective. Touched objectives get a quiet receipt line;
 * untouched get the three-way checkpoint (explicitly XP-free, AC-W15).
 * Forecast re-display reads xp.dateAdjustmentAccepted for its real value —
 * never the habit-side adjustmentAccepted field.
 */
export function ObjectivesPanel({
  objectives,
  dateAdjustmentAcceptedXp,
  onAcceptDateSuggestion,
  onAnswerCheckpoint,
  onEditObjective,
}: {
  readonly objectives: readonly ObjectiveReviewItem[];
  readonly dateAdjustmentAcceptedXp: number;
  readonly onAcceptDateSuggestion: (suggestion: ObjectiveDateSuggestionView) => Promise<void>;
  readonly onAnswerCheckpoint: (objectiveId: ObjectiveId, answer: ObjectiveCheckpointAnswer) => Promise<void>;
  readonly onEditObjective: (objectiveId: ObjectiveId) => void;
}) {
  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--layout-card-padding-desktop)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
        Objectives this week
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
        {objectives.map((objective) => (
          <ObjectiveReviewCard
            key={objective.objectiveId}
            objective={objective}
            dateAdjustmentAcceptedXp={dateAdjustmentAcceptedXp}
            onAcceptDateSuggestion={onAcceptDateSuggestion}
            onAnswerCheckpoint={onAnswerCheckpoint}
            onEditObjective={onEditObjective}
          />
        ))}
      </div>
    </section>
  );
}

function ObjectiveReviewCard({
  objective,
  dateAdjustmentAcceptedXp,
  onAcceptDateSuggestion,
  onAnswerCheckpoint,
  onEditObjective,
}: {
  readonly objective: ObjectiveReviewItem;
  readonly dateAdjustmentAcceptedXp: number;
  readonly onAcceptDateSuggestion: (suggestion: ObjectiveDateSuggestionView) => Promise<void>;
  readonly onAnswerCheckpoint: (objectiveId: ObjectiveId, answer: ObjectiveCheckpointAnswer) => Promise<void>;
  readonly onEditObjective: (objectiveId: ObjectiveId) => void;
}) {
  return (
    <div
      style={{
        background: 'var(--surface-overlay)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0, fontSize: 'var(--type-body-size)', fontWeight: 600, color: 'var(--text-primary)' }}>{objective.title}</p>
        <span className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          {objective.actionsDone} / {objective.actionsTotal}
        </span>
      </div>

      {objective.touchedThisWeek ? (
        <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          {objective.actionsDone > 0 ? '1 milestone completed this week.' : 'Touched this week.'}
        </p>
      ) : (
        <CheckpointQuestion objective={objective} onAnswerCheckpoint={onAnswerCheckpoint} onEditObjective={onEditObjective} />
      )}

      <ForecastRow
        objective={objective}
        dateAdjustmentAcceptedXp={dateAdjustmentAcceptedXp}
        onAcceptDateSuggestion={onAcceptDateSuggestion}
      />
    </div>
  );
}

function CheckpointQuestion({
  objective,
  onAnswerCheckpoint,
  onEditObjective,
}: {
  readonly objective: ObjectiveReviewItem;
  readonly onAnswerCheckpoint: (objectiveId: ObjectiveId, answer: ObjectiveCheckpointAnswer) => Promise<void>;
  readonly onEditObjective: (objectiveId: ObjectiveId) => void;
}) {
  if (objective.checkpointAnswer === 'progress') {
    return (
      <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
        Still moving, just quiet this week.
      </p>
    );
  }
  if (objective.checkpointAnswer === 'on_hold') {
    return (
      <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
        On hold for now.
      </p>
    );
  }
  if (objective.checkpointAnswer === 'rescope') {
    return (
      <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
        Re-scoping.
      </p>
    );
  }

  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>
        "{objective.title}" didn't move this week. Where does it stand?
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
        <CheckpointButton
          label="Progressing — just quiet"
          onClick={() => void onAnswerCheckpoint(objective.objectiveId, 'progress')}
        />
        <CheckpointButton
          label="Re-scope it"
          onClick={() => {
            void onAnswerCheckpoint(objective.objectiveId, 'rescope').then(() =>
              onEditObjective(objective.objectiveId),
            );
          }}
        />
        <CheckpointButton
          label="On hold for now"
          onClick={() => void onAnswerCheckpoint(objective.objectiveId, 'on_hold')}
        />
      </div>
    </div>
  );
}

function CheckpointButton({ label, onClick }: { readonly label: string; readonly onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minHeight: 'var(--touch-target-min)',
        padding: '0 var(--space-4)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-strong)',
        color: 'var(--text-secondary)',
        fontWeight: 500,
        fontSize: 'var(--type-body-size)',
        background: 'none',
      }}
    >
      {label}
    </button>
  );
}

function ForecastRow({
  objective,
  dateAdjustmentAcceptedXp,
  onAcceptDateSuggestion,
}: {
  readonly objective: ObjectiveReviewItem;
  readonly dateAdjustmentAcceptedXp: number;
  readonly onAcceptDateSuggestion: (suggestion: ObjectiveDateSuggestionView) => Promise<void>;
}) {
  const { forecast } = objective;

  if (forecast.paceActionsPerWeek === null || forecast.forecastFinishDate === null) {
    return null; // no forecast history yet — no sub-section, matches objectives.md's "insufficient history" state
  }

  const ariaLabel = `${objective.actionsDone} of ${objective.actionsTotal} done, pace about ${forecast.paceActionsPerWeek} per week, forecast finish ${formatShortDate(forecast.forecastFinishDate)}`;

  return (
    <div
      style={{
        marginTop: 'var(--space-4)',
        paddingTop: 'var(--space-3)',
        borderTop: '1px solid var(--border-subtle)',
      }}
      aria-label={ariaLabel}
    >
      {forecast.isSlipping && objective.dateSuggestion ? (
        <>
          <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
            At this pace you land past the{' '}
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{formatShortDate(objective.dateSuggestion.currentTargetDate)}</span>{' '}
            target.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
            <button
              type="button"
              onClick={() => void onAcceptDateSuggestion(objective.dateSuggestion!)}
              style={{
                minHeight: 'var(--touch-target-min)',
                padding: '0 var(--space-4)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--hue-primary-vivid)',
                color: 'var(--text-on-accent)',
                fontWeight: 600,
                fontSize: 'var(--type-body-size)',
                border: '1px solid var(--hue-primary-strong)',
              }}
            >
              Move target to {formatShortDate(objective.dateSuggestion.suggestedTargetDate)} — +{dateAdjustmentAcceptedXp} XP
            </button>
            <button
              type="button"
              style={{
                minHeight: 'var(--touch-target-min)',
                padding: '0 var(--space-4)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-strong)',
                color: 'var(--text-secondary)',
                fontWeight: 500,
                fontSize: 'var(--type-body-size)',
                background: 'none',
              }}
            >
              Keep {formatShortDate(objective.dateSuggestion.currentTargetDate)}
            </button>
          </div>
        </>
      ) : (
        <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          Remaining items fit comfortably at the current pace. No adjustment needed.
        </p>
      )}
    </div>
  );
}
